import React from 'react';
import { ProductInterface } from 'interfaces/product.interface';

interface StructuralDataProductInterface {
    '@context': string;
    '@type': string;
    name: string;
    sku: number;
    mpn?: string;
    isbn?: number[];
    ean?: string[];
    gtin13?: string;
    gtin12?: string;
    gtin8?: string;
    description?: string;
    image?: string[];
    brand?: {
        '@type': string;
        name?: string;
    };
    aggregateRating?: {
        '@type': string;
        bestRating: string;
        ratingCount: number;
        ratingValue: number;
        reviewCount: number;
        worstRating: string;
    };
    offers?: {
        '@type': string;
        availability: string;
        itemCondition: string;
        lowPrice: number;
        highPrice: number;
        offerCount: number;
        priceCurrency: string;
    } | {
        '@type': string;
        availability: string;
        itemCondition: string;
        price: number;
        priceCurrency: string;
    };
    review?: {
        '@type': string;
        name: string;
        datePublished: string;
        reviewRating: {
            '@type': string;
            ratingValue: number;
            bestRating: string;
            worstRating: string;
        };
        author: {
            '@type': string;
            name: string;
        };
        reviewBody: string;
    } | {
        '@type': string;
        name: string;
        datePublished: string;
        reviewRating: {
            '@type': string;
            ratingValue: number;
            bestRating: string;
            worstRating: string;
        };
        author: {
            '@type': string;
            name: string;
        };
        reviewBody: string;
    }[];
}

const ProductStructuralData: React.FC<{product: ProductInterface;}> = ({ product }) => {
    if (!product) {
        return null;
    }

    const MAX_PHOTOS = 10;

    const data: StructuralDataProductInterface = {
        '@context': 'http://schema.org/',
        '@type': 'Product',
        'name': product.name,
        'sku': product.id
    };

    if (product.partNo) {
        data.mpn = product.partNo;
    }

    if (product.isbn?.[0]) {
        data.isbn = product.isbn;
    }

    if (Array.isArray(product.ean)) {
        product.ean.forEach(item => {
            if (!data.gtin13 && item.length === 13) {
                data.gtin13 = item;
            }

            if (!data.gtin12 && item.length === 12) {
                data.gtin12 = item;
            }

            if (!data.gtin8 && item.length === 8) {
                data.gtin8 = item;
            }
        });
    }

    if (product.description?.text) {
        data.description = product.description.text;
    }

    if (product.photos.items.length) {
        data.image = product.photos.items.slice(0, MAX_PHOTOS).map(img => img.large);
    }

    if (product.producer?.name) {
        data.brand = {
            '@type': 'Brand',
            'name': product.producer?.name
        };
    }

    if (product.opinions?.count) {
        data.aggregateRating = {
            '@type': 'AggregateRating',
            'bestRating': '5.0',
            'ratingCount': product.opinions.count,
            'ratingValue': product.opinions.average,
            'reviewCount': product.opinions.count,
            'worstRating': '1.0'
        };
    }

    if (product.offers.count > 0) {
        data.offers = {
            '@type': 'AggregateOffer',
            'availability': 'http://schema.org/InStock',
            'itemCondition': 'http://schema.org/NewCondition',
            'lowPrice': product.minPrice,
            'highPrice': product.maxPrice,
            'offerCount': product.offers.count,
            'priceCurrency': 'PLN'
        };
    } else if (product.minPrice > 0 || product.maxPrice > 0) {
        data.offers = {
            '@type': 'AggregateOffer',
            'availability': 'http://schema.org/OutOfStock',
            'itemCondition': 'http://schema.org/NewCondition',
            'lowPrice': product.minPrice,
            'highPrice': product.maxPrice,
            'offerCount': 0,
            'priceCurrency': 'PLN'
        };
    }

    if (product?.opinions?.items.length > 0) {
        if (product?.opinions?.items.length === 1) {
            const opinionsIitem = product.opinions?.items?.[0];

            data.review = {
                '@type': 'Review',
                'datePublished': new Date(opinionsIitem.timestamp * 1000).toISOString().slice(0, 10),
                'name': `${product.name} - opinie`,
                'reviewBody': opinionsIitem.content,
                'author': {
                    '@type': 'Person',
                    'name': opinionsIitem.author
                },
                'reviewRating': {
                    '@type': 'Rating',
                    'ratingValue': opinionsIitem.rating,
                    'bestRating': '5.0',
                    'worstRating': '1.0'
                }
            };
        } else {
            data.review = product?.opinions?.items.slice(0, 10).map((item): any => ({
                '@type': 'Review',
                'datePublished': new Date(item.timestamp * 1000).toISOString().slice(0, 10),
                'name': `${product.name} - opinie`,
                'reviewBody': item.content,
                'author': {
                    '@type': 'Person',
                    'name': item.author
                },
                'reviewRating': {
                    '@type': 'Rating',
                    'ratingValue': item.rating,
                    'bestRating': '5.0',
                    'worstRating': '1.0'
                }
            }));
        }
    }

    if (!data.review && !data.offers && !data.aggregateRating) {
        return null;
    }

    return (
        <script
            dangerouslySetInnerHTML={{ __html: JSON.stringify(data) }}
            type="application/ld+json"
        />
    );
};

export default ProductStructuralData;
